// Define a class for the player spaceship

import {Sprite} from "pixi.js";
import {Polygon} from "detect-collisions";
import {Vector} from "./i.js"; // ./vector.js
import {logger} from "./i.js"; // ./logger.js
import {angleDiff, lerp, MOUSE, RES, sq} from "./i.js"; // ./dump.js
import {keyboard, Keyboard} from "./i.js"; // ./keyboard.js
import {ConfigStructure, OptionalProperty} from "./i.js"; // ./validators.js
import {AI, AIUtils} from "./i.js"; // ./ai.js
import {Thing, STANDARD_PPF, THING_TYPES, Z_INDICES} from "./i.js"; // ./thing.js
import {Nanobots} from "./i.js"; // ./nanobots.js
import {Bullet} from "./i.js"; // ./bullet.js
import {ACHIEVEMENTS} from "./i.js";

// TODO: I just moved move() (no pun intended) from the Player class... A lot more could
// be moved to the AI from the Player class, and refactored in smart ways.
export class PlayerAI extends AI {
	static CONFIG_STRUCTURE = new ConfigStructure([]);

	// Cooldown between successive heals, in seconds
	static HEALING_COOLDOWN = 5;
	// Number of healing swarms to spawn per heal
	static HEALING_SWARMS = 10;

	constructor(thing, config) {
		PlayerAI.CONFIG_STRUCTURE.test(config);

		super(thing);

		this.healingCooldown = 0;

		// Set by cossette
		this.tractorBeamAcc = new Vector();
	}

	// Move the player based on input from mouse and keyboard
	// dt is the time since last update, in seconds. This function only alters
	// true position of the player; since the player is always centered on the
	// screen, its screen position is constant and doesn't need updating. This
	// function returns player's offset, which can then be applied backwards to
	// all other things to achieve the illusion of movement.
	move(dt, time) {
		// Don't move if the player is in limbo
		if (this.thing.state === Thing.STATES.LIMBO) return;

		if (this.thing.pos.mag() > 400) {
			this.thing.space.game.unicenter.ship.achievements.recordAchievement(
				ACHIEVEMENTS.ESCAPE
			);
		}

		let nextMaxHealth = this.thing.space.game.unicenter.upgrader
			.getValue("HEALTH");
		if (this.thing.maxHealth != nextMaxHealth) {
			this.thing.health = (this.thing.health / this.thing.maxHealth) * nextMaxHealth;
			this.thing.maxHealth = nextMaxHealth;
			// TODO: Find a cleaner way to update the health bar
			this.thing.healthBar._maxVal = this.thing.maxHealth;
			this.thing.healthBar.val = this.thing.health;
		}

		let actions = keyboard.getActions("PLAYER");

		// Begin docking
		if (actions.has("DOCK")) this.thing.attemptToDock();

		if (this.thing.dockingTo === null) {
			// Move freely

			// Turn towards the mouse
			let position = new Vector(
				window.innerWidth / 2,
				window.innerHeight / 2
			);
			AIUtils.rotateTo(this.thing, Vector.diff(MOUSE, position).dir(), Player.ROT_SPEED,
							dt);

			// Move according to the keyboard
			let speed = this.thing.space.game.unicenter.upgrader.getValue("SPEED");
			let thrustersForged = this.thing.space.game.unicenter.upgrader
				.thrustersForged;
			let thrustAcc = speed * Player.FRICTION;
			let brakeAcc = thrustersForged ? (0.5 * thrustAcc) : 0;
			let slideAcc = thrustersForged ? (0.5 * thrustAcc) : 0;
			let accForward = (actions.has("THRUST") ? thrustAcc : 0)
							- (actions.has("BRAKE") ? brakeAcc : 0);
			let accLeft = (actions.has("SLIDE_LEFT") ? slideAcc : 0)
							- (actions.has("SLIDE_RIGHT") ? slideAcc : 0);
			let accX = accForward * Math.cos(this.thing.rot)
							+ accLeft * Math.sin(this.thing.rot);
			let accY = accForward * Math.sin(this.thing.rot)
							- accLeft * Math.cos(this.thing.rot);
			let acc = new Vector(accX, accY);
			acc.add(this.tractorBeamAcc);
			AIUtils.translate(this.thing, acc, Player.FRICTION, dt);

			this.thing._exhaust.visible = actions.has("THRUST");
		} else {
			// Dock to a station

			this.thing._exhaust.visible = false;

			let station = this.thing._getStation(this.thing.dockingTo);

			// Increase _dockFraction
			if (this.thing._dockFraction === null) {
				logger.error(
					"player.js",
					"_dockFraction unexpectedly null"
				);
				this.thing._dockFraction = 0;
			}
			this.thing._dockFraction += dt / Player.TIME_TO_DOCK;
			this.thing._dockFraction = Math.min(this.thing._dockFraction, 1);
			let remainingTime = Player.TIME_TO_DOCK * (1 - this.thing._dockFraction);

			let [targetPos, targetRot] = station.getDockLocation();

			// When numbers become this small, snap to the final location
			const EPSILON = 0.01;

			// Increase acceleration early, so that the player covers most of
			// the distance early, and approaches the dock at a low speed at the
			// end
			let accOverclock = 1 + remainingTime / Player.TIME_TO_DOCK;

			// Translate
			let leftToGo = Vector.diff(targetPos, this.thing.pos);
			let acc = new Vector();
			if (leftToGo.mag() > EPSILON && remainingTime > EPSILON) {
				// Calculate acceleration assuming no friction
				// This formula comes from rearranging x = v_0*t + a*t^2/2.
				let accMag = 2 * (leftToGo.mag()
								- Vector.proj(this.thing.vel, leftToGo).mag()
								* remainingTime) / sq(remainingTime);
				accMag *= accOverclock;
				acc = Vector.norm(leftToGo, accMag);
				AIUtils.translate(this.thing, acc, Player.FRICTION, dt);
			} else {
				// Snap to the final location, set vel for external stuff
				this.thing.vel.copyFrom(Vector.diff(targetPos, this.thing.pos));
				this.thing.pos.copyFrom(targetPos);
			}

			// Rotate
			let leftToRotate = angleDiff(targetRot, this.thing.rot);
			let rotAcc = 0;
			if (Math.abs(leftToRotate) > EPSILON && remainingTime > EPSILON) {
				// Calculate rotational acceleration assuming no friction
				// This formula comes from rearranging x = v_0*t + a*t^2/2.
				rotAcc = 2 * (leftToRotate - this.thing.spn * remainingTime)
								/ sq(remainingTime);
				rotAcc *= accOverclock;
				this.thing.spn += rotAcc * dt;
				AIUtils.rotateBy(this.thing, this.thing.spn, dt);
			} else {
				// Snap to the final location, set spn for external stuff
				this.thing.spn = targetRot - this.thing.rot;
				this.thing.rot = targetRot;
			}

			if (remainingTime === 0) this.thing.space.game.unicenter.show();
		}

		// Energy & weaponry

		let firing = this.thing.firingMouse || actions.has("FIRE");
		if (firing && this.thing.cooldown === 0
						&& this._consumeEnergy(Player.FIRING_COST)) {
			this.thing.space.requestSpawn(Bullet, {
				pos: this.thing.pos,
				rot: this.thing.rot,
				parentVel: this.thing.vel
			});
			this.thing.cooldown = Player.COOLDOWN;
		}

		this.thing._timeSinceEnergyUsed += dt;
		if (this.thing._timeSinceEnergyUsed >= 0.75) {
			this.thing.energy += (Player.POWER * dt);
		}
		this.thing.energy = Math.min(this.thing.energy, Player.MAX_ENERGY);
		this.thing.cooldown -= dt;
		this.thing.cooldown = Math.max(this.thing.cooldown, 0);

		// Energy sphere
		let absoluteMaxScale = 1 / STANDARD_PPF;
		let minScale = (0.04 / 0.18) * absoluteMaxScale;
		let maxScale = (0.16 / 0.18) * absoluteMaxScale;
		this.thing._sphere.scale.set(lerp(minScale, maxScale,
						this.thing.energy / Player.MAX_ENERGY));

		this._handleHealing(actions, dt, time);
	}

	_handleHealing(actions, dt, time) {
		this.healingCooldown -= dt;
		if (this.healingCooldown <= 0) {
			this.healingCooldown = 0;
			this.thing.vulnerable = false;
		}

		if (this.healingCooldown > 0) {
			let desiredSwarms = Math.min(
				Math.floor(
					(PlayerAI.HEALING_COOLDOWN - this.healingCooldown) / 0.03
				) + 1,
				PlayerAI.HEALING_SWARMS
			);

			// How many swarms we would have desired last tick
			let spawnedSwarms = Math.min(
				Math.floor(
					(PlayerAI.HEALING_COOLDOWN - (this.healingCooldown + dt)) / 0.03
				) + 1,
				PlayerAI.HEALING_SWARMS
			);

			while (spawnedSwarms < desiredSwarms) {
				this.thing.space.requestSpawn(Nanobots, {
					parentId: this.thing.id,
					relativeDir: - 2 * Math.PI * spawnedSwarms
									/ PlayerAI.HEALING_SWARMS
				});
				++spawnedSwarms;
			}
		}

		let wantsToHealKeyboard = actions.has("HEAL");
		let wantsToHealMouse = this.thing.space.game.dashboard.healingButton.wasClicked();
		let wantsToHeal = wantsToHealKeyboard || wantsToHealMouse;
		if (wantsToHeal && this.healingCooldown <= 0
						&& this._consumeEnergy(70)) {
			// TODO: The tiny addition is a hack to avoid an off-by-one error in the
			// spawnedSwarms calculation
			this.healingCooldown = PlayerAI.HEALING_COOLDOWN + 0.0001;
			this.thing.vulnerable = true;
		}
	}

	// Return true iff there was enough energy
	_consumeEnergy(amount) {
		if (this.thing.energy < amount) return false;

		this.thing.energy -= amount;
		this.thing._timeSinceEnergyUsed = 0;
		return true;
	}
}



export class Player extends Thing {
	static CONFIG_STRUCTURE = new ConfigStructure([
		// pos of the player, a vector with values in flares
		new OptionalProperty("pos", v => (v instanceof Vector)),
		// vel of the player, a vector with values in flares / second
		new OptionalProperty("vel", v => (v instanceof Vector)),
		// rot of the player, in radians
		new OptionalProperty("rot", v => (Number.isFinite(v))),
		// Health of the player
		new OptionalProperty("health", v => (Number.isFinite(v) && v >= 0)),
		// id of the station the player is docking to, null if none
		new OptionalProperty("dockingTo", v =>
						(v === null || Number.isFinite(v))),
		// Fraction of the docking process completed, from 0 to 1, or null if
		// not docked
		new OptionalProperty("dockFraction", v =>
						(v === null
						|| (Number.isFinite(v) && v >= 0 && v <= 1))),
		// Id of the last visited station
		new OptionalProperty("lastVisitedStationId", v => (Number.isFinite(v)))
	]);

	// Rotation speed, radians / second
	static ROT_SPEED = 12;
	// Friction, 1 / second
	// Max speed is <direction>_ACC / FRICTION.
	static FRICTION = 4;
	// Max energy (for now, energy is only used for the weapon)
	static MAX_ENERGY = 100;
	// Power, AKA the rate at which the energy regenerates, in energy / second
	static POWER = 100;
	// Energy cost to fire the blaster
	static FIRING_COST = 10;
	// Cooldown of the weapon, in seconds
	static COOLDOWN = 0.2;
	// Time needed to dock to a station, in seconds
	static TIME_TO_DOCK = 1;

	// Create a player
	// config is an object with the properties provided by the spawn request
	// enrichment, plus the properties specified in CONFIG_STRUCTURE above.
	constructor(config) {
		Player.CONFIG_STRUCTURE.test(config);

		config.zIndex         = Z_INDICES.PLAYER;
		config.len            = 0.5;
		config.hitbox         = new Polygon({x: 0, y: 0}, [
			{x: 0.25, y: 0},
			{x: -0.25, y: -0.15},
			{x: -0.25, y: 0.15}
		]);
		config.type           = THING_TYPES.PLAYER;
		config.canDespawn     = false;
		config.canPickUpItems = true;
		config.gpsIcon        = "images/icons/gps/player.svg";
		config.rotateGpsIcon  = true;
		if (config.pos === undefined) config.pos = new Vector();
		if (config.vel === undefined) config.vel = new Vector();
		if (config.rot === undefined) config.rot = 0;
		config.maxHealth = config.space.game.unicenter.upgrader.getValue("HEALTH");
		super(config);

		this.ai = new PlayerAI(this, {});

		// TODO: Calculate this dynamically?
		this.vulnerable = false;

		this._hull = new Sprite(RES["images/player/hull.svg"].texture);
		this._hull.anchor.set(0.5, 0.5);
		this._hull.position.set(0, 0);
		this._hull.scale.set(1 / STANDARD_PPF);
		this.display.addChild(this._hull);

		this._exhaust = new Sprite(
			RES["images/player/exhaust.svg"].texture
		);
		this._exhaust.anchor.set(0.5, 0);
		this._exhaust.position.set(0, 0.17);
		this._exhaust.scale.set(1 / STANDARD_PPF);
		this.display.addChild(this._exhaust);

		this._sphere = new Sprite(RES["images/player/sphere.svg"].texture);
		this._sphere.anchor.set(0.5, 0.5);
		this._sphere.position.set(0, 0.17);
		this.display.addChild(this._sphere);

		// Set weapon-related properties
		this.firingMouse = false;
		this.energy = Player.MAX_ENERGY;
		this._timeSinceEnergyUsed = 0;
		this.cooldown = 0;

		// id of the station the player is docking to, null if none
		this.dockingTo = config.dockingTo ?? null;
		// Fraction of the docking process completed, from 0 to 1, or null if
		// not docked
		this._dockFraction = config.dockFraction ?? null;

		this._lastVisitedStationId = config.lastVisitedStationId ?? null;

		// Add health bar
		this.healthBar = this.space.scanner.addBar(
			this.display.position,
			this.radius + 10,
			this.health,
			this.maxHealth
		);

		// TODO: Pop this control when it's no longer needed
		keyboard.pushControl(new Keyboard.Control(
			"PLAYER",
			new Map([
				["_UP",    "THRUST"     ],
				["_DOWN",  "BRAKE"      ],
				["_LEFT",  "SLIDE_LEFT" ],
				["_RIGHT", "SLIDE_RIGHT"],
				["_FIRE",  "FIRE"       ],
				[".DOCK",  "DOCK"       ],
				["_HEAL",  "HEAL"       ]
			]),
			new Set()
		));
	}

	// Redraw the player on the screen
	// This function overrides Thing.redraw().
	redraw(screenSize, pixelsPerFlare) {
		super.redraw(
			screenSize,
			pixelsPerFlare,
			new Vector(screenSize.x / 2, screenSize.y / 2)
		);
	}

	// Attempt to dock to a nearby station
	// You don't need to specify what station to dock to - this function finds
	// a nearby station automatically, if there's any. This function does
	// nothing if the player is already docked, or if there's no nearby station.
	attemptToDock() {
		if (this.dockingTo !== null || !this.space.canDock) return;

		let tacticalReport = this.space.requestTacticalReport(
			entry => (entry.TYPE === THING_TYPES.STATION),
			"DOCK"
		);

		let minDistance = Infinity;
		let stationId = null;
		tacticalReport.forEach(entry => {
			let dist = Vector.diff(this.pos, entry.pos).mag();
			if (dist < minDistance) {
				minDistance = dist;
				stationId = entry.id;
			}
		});

		if (stationId === null) {
			logger.error("player.js", "can't find station to dock to");
		}
		this.dockingTo = stationId;
		this._dockFraction = 0;
		this._getStation(stationId).dockExtended = true;
	}

	// Undock from a station
	// This function does nothing if the player isn't docked. Called by the
	// unicenter.
	undock() {
		if (this.dockingTo === null) return;

		this.dockingTo = null;
		this._dockFraction = null;
	}

	// Return the index of the station the player is fully docked to, or null
	dockedTo() {
		if (this.dockingTo === null) return null;
		return (this._dockFraction === 1) ? this.dockingTo : null;
	}

	// Take damage from an attack
	// damage is a number.
	takeDamage(damage) {
		if (this.vulnerable) damage *= 3;

		if (this.health > damage) {
			this._changeHealth(this.health - damage);
		} else {
			// Die!
			this._changeHealth(0);
			this.prepareToDie(false);
		}
	}

	// Repair the player a little bit
	// dt is the time since last update, in seconds. The interactor executes
	// this function every tick if the player is inside a station. stationId is the id of
	// the station providing the repairs.
	repair(dt, stationId) {
		this._changeHealth(Math.min(this.health + this.maxHealth * dt,
						this.maxHealth));
		this._lastVisitedStationId = stationId;
	}

	// Receive a drop
	receiveDrop(drop) {
		this.space.game.unicenter.inventory.add(drop);
	}

	// Save this player into a config object
	// See the comment on Thing.save() for more details.
	save() {
		let config = super.save().config;

		config.dockingTo = this.dockingTo;
		// TODO: this prop must be null iff not docked, enforce it in some way
		config.dockFraction = this._dockFraction;
		config.lastVisitedStationId = this._lastVisitedStationId;

		return {
			config:     config,
			isComplete: true
		};
		// TODO: Add this._timeSinceEnergyUsed?
	}

	// Set up this player to respawn
	prepareToDie(dropItems) {
		// Already prepared
		if (this.state === Thing.STATES.LIMBO) return;

		super.prepareToDie(dropItems, true);

		// TODO: Shouldn't modify private properties here
		this.space.scanner._arrows.visible = false;

		// Lose all items on death
		this.space.game.unicenter.inventory.clearAllItems();

		this.space.game.respawner.show();
	}

	// Respawn from limbo
	// Called by respawner.
	respawn() {
		super.respawn();
		let lastVisitedStation = this.space.requestThingById(this._lastVisitedStationId);
		if (lastVisitedStation === null) {
			this.pos = new Vector();
		} else {
			this.pos.copyFrom(lastVisitedStation.pos);
		}
		this.rot = 0;
		this.health = this.maxHealth;
		this.energy = Player.MAX_ENERGY;
		this.cooldown = 0;
		// TODO: Shouldn't modify private properties here
		this.space.scanner._arrows.visible = true;
	}

	// Change health to nextHealth, and update all visual indicators of health
	_changeHealth(nextHealth) {
		this.health = nextHealth;
		this.healthBar.val = this.health;
	}

	// Get a reference to a station from its id
	// Complain if station not found.
	_getStation(id) {
		let station = this.space.requestThingById(id);

		// In the future, if stations can be destroyed, probably just undock
		// in this case
		if (station === null) logger.error(
			"player.js",
			`station with id ${id} not found`
		);

		return station;
	}
}
